<script setup>
// Modo Telão: a TV da clínica mostra, por profissional, quem está sendo atendido
// agora e quem vem a seguir. Fica aberta o dia todo sem ninguém mexer: atualiza a
// agenda sozinha, troca de horário com o relógio e alterna páginas quando não cabe.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { googleCalendarAPI } from '../../../api/googleCalendar';
import {
  montarCartoes,
  paginar,
  colunasPara,
  formatarHora,
} from './helpers/telao';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const CARTOES_POR_PAGINA = 16;
const ATUALIZAR_AGENDA_MS = 60 * 1000;
const TROCAR_PAGINA_MS = 12 * 1000;
const RELOGIO_MS = 10 * 1000;
const ESCONDER_CONTROLES_MS = 3000;

const agendas = ref([]);
const eventos = ref([]);
const agora = ref(new Date());
const pagina = ref(0);
const carregouUmaVez = ref(false);
const semConexao = ref(false);
const controlesVisiveis = ref(false);

let timerAgenda = null;
let timerPagina = null;
let timerRelogio = null;
let timerControles = null;
let wakeLock = null;

const cartoes = computed(() => montarCartoes(eventos.value, agora.value));
const paginas = computed(() => paginar(cartoes.value, CARTOES_POR_PAGINA));
const paginaAtual = computed(
  () => paginas.value[Math.min(pagina.value, paginas.value.length - 1)] || []
);
const colunas = computed(() => colunasPara(paginaAtual.value.length));

// Tamanhos em vw: a mesma tela serve para TV de 43" ou 75" e o texto cresce
// quando há poucos cartões.
const escala = computed(
  () => ({ 1: 2.4, 2: 1.6, 3: 1.2, 4: 1 })[colunas.value]
);
const estiloGrade = computed(() => ({
  gridTemplateColumns: `repeat(${colunas.value}, minmax(0, 1fr))`,
}));
const fonte = base => ({ fontSize: `${(base * escala.value).toFixed(2)}vw` });

const relogio = computed(() => formatarHora(agora.value));
const dataPorExtenso = computed(() =>
  agora.value.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
);

const horario = atendimento =>
  `${formatarHora(atendimento.inicio)} – ${formatarHora(atendimento.fim)}`;

function intervaloDoDia() {
  const inicio = new Date();
  inicio.setHours(0, 0, 0, 0);
  const fim = new Date(inicio);
  fim.setHours(23, 59, 59, 999);
  return { inicio: inicio.toISOString(), fim: fim.toISOString() };
}

// Todas as agendas, sem depender das que estão marcadas na tela normal do calendário.
async function carregarAgenda() {
  try {
    if (!agendas.value.length) {
      const res = await googleCalendarAPI.getCalendars();
      agendas.value = Array.isArray(res.data) ? res.data : [];
    }
    const { inicio, fim } = intervaloDoDia();
    const resultados = await Promise.all(
      agendas.value.map(async agenda => {
        try {
          const res = await googleCalendarAPI.getEvents(agenda.id, inicio, fim);
          return (Array.isArray(res.data) ? res.data : []).map(ev => ({
            ...ev,
            calendarId: agenda.id,
            calendarName: agenda.summary || agenda.id,
            calendarColor: agenda.backgroundColor || '#00AEEF',
          }));
        } catch {
          return null;
        }
      })
    );
    // Uma agenda que falhou nesta volta mantém o que já se sabia dela, em vez de
    // sumir da TV por um minuto.
    const anteriores = eventos.value;
    eventos.value = resultados.flatMap((lista, i) =>
      lista === null
        ? anteriores.filter(ev => ev.calendarId === agendas.value[i].id)
        : lista
    );
    semConexao.value = resultados.some(r => r === null);
  } catch {
    semConexao.value = true;
  } finally {
    carregouUmaVez.value = true;
  }
}

function avancarPagina() {
  pagina.value =
    paginas.value.length > 1 ? (pagina.value + 1) % paginas.value.length : 0;
}

// A TV não pode apagar a tela por inatividade. Sem suporte, segue sem a trava.
async function manterTelaAcesa() {
  try {
    if ('wakeLock' in navigator && document.visibilityState === 'visible') {
      wakeLock = await navigator.wakeLock.request('screen');
    }
  } catch {
    wakeLock = null;
  }
}

function aoMudarVisibilidade() {
  if (document.visibilityState === 'visible') {
    manterTelaAcesa();
    carregarAgenda();
  }
}

function mostrarControles() {
  controlesVisiveis.value = true;
  clearTimeout(timerControles);
  timerControles = setTimeout(() => {
    controlesVisiveis.value = false;
  }, ESCONDER_CONTROLES_MS);
}

function telaCheia() {
  const el = document.documentElement;
  if (document.fullscreenElement) return;
  el.requestFullscreen?.().catch(() => {});
}

function sair() {
  if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  router.push({
    name: 'google_calendar',
    params: { accountId: route.params.accountId },
  });
}

function aoTeclar(e) {
  if (e.key === 'Escape' && !document.fullscreenElement) sair();
}

onMounted(() => {
  carregarAgenda();
  manterTelaAcesa();
  timerAgenda = setInterval(carregarAgenda, ATUALIZAR_AGENDA_MS);
  timerPagina = setInterval(avancarPagina, TROCAR_PAGINA_MS);
  timerRelogio = setInterval(() => {
    agora.value = new Date();
  }, RELOGIO_MS);
  document.addEventListener('visibilitychange', aoMudarVisibilidade);
  window.addEventListener('keydown', aoTeclar);
});

onBeforeUnmount(() => {
  [timerAgenda, timerPagina, timerRelogio].forEach(clearInterval);
  clearTimeout(timerControles);
  document.removeEventListener('visibilitychange', aoMudarVisibilidade);
  window.removeEventListener('keydown', aoTeclar);
  wakeLock?.release?.().catch(() => {});
});
</script>

<template>
  <div
    class="fixed inset-0 z-[100] flex flex-col bg-slate-950 text-white select-none"
    :class="{ 'cursor-none': !controlesVisiveis }"
    @mousemove="mostrarControles"
    @click="mostrarControles"
  >
    <!-- Cabeçalho -->
    <header
      class="flex items-end justify-between px-[2vw] pt-[1.5vw] pb-[1vw] border-b border-white/10"
    >
      <div>
        <p
          class="font-bold tracking-wide text-[#00AEEF] uppercase text-[1.6vw] leading-none"
        >
          {{ t('GOOGLE_CALENDAR.TELAO.NOW') }}
        </p>
        <p class="mt-[0.4vw] text-[1.1vw] text-white/60 first-letter:uppercase">
          {{ dataPorExtenso }}
        </p>
      </div>
      <div class="flex items-end gap-[1.5vw]">
        <span
          v-if="semConexao"
          class="flex items-center gap-[0.4vw] text-[0.9vw] text-amber-300"
        >
          <span class="i-lucide-wifi-off size-[1.1vw]" />
          {{ t('GOOGLE_CALENDAR.TELAO.OFFLINE') }}
        </span>
        <span class="font-bold tabular-nums leading-none text-[4vw]">
          {{ relogio }}
        </span>
      </div>
    </header>

    <!-- Cartões -->
    <main class="flex-1 min-h-0 p-[1.5vw]">
      <div
        v-if="!carregouUmaVez"
        class="h-full flex items-center justify-center text-[2vw] text-white/50"
      >
        {{ t('GOOGLE_CALENDAR.TELAO.LOADING') }}
      </div>
      <div
        v-else-if="!cartoes.length"
        class="h-full flex flex-col items-center justify-center gap-[1vw] text-white/60"
      >
        <span class="i-lucide-calendar-check size-[6vw] text-[#35C28C]" />
        <p class="text-[2.6vw] font-semibold">
          {{ t('GOOGLE_CALENDAR.TELAO.EMPTY') }}
        </p>
      </div>
      <div
        v-else
        class="grid h-full gap-[1.2vw] auto-rows-fr"
        :style="estiloGrade"
      >
        <article
          v-for="cartao in paginaAtual"
          :key="cartao.agendaId"
          class="flex flex-col justify-between min-h-0 overflow-hidden rounded-[0.8vw] bg-white/[0.06] border-l-[0.5vw] px-[1.4vw] py-[1vw]"
          :style="{ borderLeftColor: cartao.cor }"
        >
          <p
            class="font-semibold uppercase tracking-wide text-white/70 truncate"
            :style="fonte(1.1)"
          >
            {{ cartao.profissional }}
          </p>

          <div v-if="cartao.atual" class="min-h-0 leading-tight">
            <p
              class="font-extrabold line-clamp-2 break-words"
              :style="fonte(2.4)"
            >
              {{ cartao.atual.paciente }}
            </p>
            <p
              class="font-semibold tabular-nums text-[#00AEEF]"
              :style="fonte(1.3)"
            >
              {{ horario(cartao.atual) }}
            </p>
          </div>
          <p v-else class="font-semibold text-white/40" :style="fonte(1.5)">
            {{ t('GOOGLE_CALENDAR.TELAO.FREE') }}
          </p>

          <p
            v-if="cartao.proximo"
            class="truncate text-white/60 border-t border-white/10 pt-[0.5vw]"
            :style="fonte(1)"
          >
            <span class="text-[#35C28C] font-semibold">
              {{ t('GOOGLE_CALENDAR.TELAO.NEXT') }}
            </span>
            {{ cartao.proximo.paciente }} ·
            <span class="tabular-nums">{{
              formatarHora(cartao.proximo.inicio)
            }}</span>
          </p>
        </article>
      </div>
    </main>

    <!-- Páginas -->
    <footer
      v-if="paginas.length > 1"
      class="flex justify-center gap-[0.6vw] pb-[1vw]"
    >
      <span
        v-for="(_, i) in paginas"
        :key="i"
        class="size-[0.7vw] rounded-full"
        :class="i === pagina ? 'bg-[#00AEEF]' : 'bg-white/20'"
      />
    </footer>

    <!-- Controles: só aparecem ao mexer o mouse -->
    <div
      class="absolute top-[1vw] right-[1vw] flex gap-2 transition-opacity duration-300"
      :class="
        controlesVisiveis ? 'opacity-100' : 'opacity-0 pointer-events-none'
      "
    >
      <button
        class="flex items-center gap-1 px-3 py-2 rounded bg-white/10 hover:bg-white/20 text-sm"
        @click.stop="telaCheia"
      >
        <span class="i-lucide-maximize size-4" />
        {{ t('GOOGLE_CALENDAR.TELAO.FULLSCREEN') }}
      </button>
      <button
        class="flex items-center gap-1 px-3 py-2 rounded bg-white/10 hover:bg-white/20 text-sm"
        @click.stop="sair"
      >
        <span class="i-lucide-x size-4" />
        {{ t('GOOGLE_CALENDAR.TELAO.EXIT') }}
      </button>
    </div>
  </div>
</template>
